import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, User, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Cards" },
  { to: "/prints", label: "Prints" },
  { to: "/services", label: "Services" },
  { to: "/gift-cards", label: "Gift Cards" },
  { to: "/wholesale", label: "Wholesale" },
  { to: "/faq", label: "FAQ" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef(null);
  const { user, logout } = useAuth();
  const { totals, setOpen: setCartOpen } = useCart();
  const loc = useLocation();

  // Close the account dropdown on outside click / Escape / route change
  useEffect(() => {
    if (!accountOpen) return;
    const onClick = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    };
    const onKey = (e) => e.key === "Escape" && setAccountOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [accountOpen]);

  useEffect(() => {
    setAccountOpen(false);
  }, [loc.pathname]);

  const isWholesaleApproved = user?.role === "wholesale" && user?.status === "approved";

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-brand-border/60 backdrop-blur-xl"
      style={{ background: "rgba(255,255,255,0.82)" }}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between gap-6">
        <Link to="/" data-testid="logo-link" className="flex items-center leading-none shrink-0">
          <img
            src="/brand/island-studio-logo.png"
            alt="The Island Studio · Nassau, The Bahamas"
            className="h-16 md:h-20 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[12px] tracking-[0.16em] uppercase font-medium transition-colors ${
                  isActive ? "text-brand-accent" : "text-brand-dark hover:text-brand-accent"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <button
            data-testid="header-cart-btn"
            onClick={() => setCartOpen(true)}
            className="relative text-brand-dark hover:text-brand-accent transition-colors flex items-center"
            aria-label="Open order request"
          >
            <ShoppingBag strokeWidth={1.5} className="w-7 h-7" />
            {totals.count > 0 && (
              <span data-testid="header-cart-count" className="absolute -top-2 -right-2 bg-brand-accent text-white text-[10px] leading-none px-1.5 py-1 rounded-full font-medium">
                {totals.count}
              </span>
            )}
          </button>
          <Link to="/quote" data-testid="header-quote-cta" className="btn-primary-sm">
            Get a Quote
          </Link>
          {user ? (
            isWholesaleApproved ? (
              <div ref={accountRef} className="relative">
                <button
                  data-testid="header-account-menu-btn"
                  onClick={() => setAccountOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  aria-label="Account menu"
                  className={`text-brand-dark hover:text-brand-accent transition-colors flex items-center ${
                    accountOpen ? "text-brand-accent" : ""
                  }`}
                >
                  <User strokeWidth={1.5} className="w-7 h-7" />
                </button>
                {accountOpen && (
                  <div
                    data-testid="header-account-menu"
                    role="menu"
                    className="absolute right-0 mt-3 w-56 bg-white border border-brand-border shadow-lg py-2"
                  >
                    <div className="px-4 py-3 border-b border-brand-border">
                      <p className="text-[10px] tracking-[0.24em] uppercase text-brand-neutral">Signed in</p>
                      <p className="mt-1 font-serif text-brand-dark truncate">
                        {user.business_name || user.name || user.email}
                      </p>
                    </div>
                    <Link
                      to="/account"
                      data-testid="header-account-link"
                      role="menuitem"
                      onClick={() => setAccountOpen(false)}
                      className="block px-4 py-3 text-sm text-brand-dark hover:bg-[#F4F2EC] hover:text-brand-accent transition-colors"
                    >
                      My Account
                    </Link>
                    <button
                      data-testid="header-logout-btn"
                      role="menuitem"
                      onClick={() => {
                        setAccountOpen(false);
                        logout();
                      }}
                      className="w-full text-left block px-4 py-3 text-sm text-brand-neutral hover:bg-[#F4F2EC] hover:text-brand-accent transition-colors"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3">
                {user.role === "admin" && (
                  <Link to="/admin" data-testid="header-admin-link" className="text-[13px] uppercase tracking-widest text-brand-accent">
                    Admin
                  </Link>
                )}
                <button
                  data-testid="header-logout-btn"
                  onClick={logout}
                  className="text-[13px] uppercase tracking-widest text-brand-neutral hover:text-brand-accent"
                >
                  Sign out
                </button>
              </div>
            )
          ) : (
            <Link data-testid="header-signin-link" to="/login" className="text-brand-neutral hover:text-brand-accent flex items-center" aria-label="Sign in">
              <User strokeWidth={1.5} className="w-7 h-7" />
            </Link>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-1">
          <button
            data-testid="mobile-cart-btn"
            onClick={() => setCartOpen(true)}
            className="relative p-2 text-brand-dark"
            aria-label="Open order request"
          >
            <ShoppingBag strokeWidth={1.5} className="w-5 h-5" />
            {totals.count > 0 && (
              <span className="absolute top-0 right-0 bg-brand-accent text-white text-[10px] leading-none px-1.5 py-1 rounded-full font-medium">
                {totals.count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="p-2 text-brand-dark"
            aria-label="Menu"
          >
            {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-brand-border bg-white">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={`text-sm uppercase tracking-widest ${loc.pathname === l.to ? "text-brand-accent" : "text-brand-dark"}`}
              >
                {l.label}
              </Link>
            ))}
            <Link to="/quote" onClick={() => setOpen(false)} data-testid="mobile-quote-cta" className="btn-primary w-fit mt-2">
              Get a Quote
            </Link>
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="text-sm uppercase tracking-widest text-brand-accent">
                    Admin
                  </Link>
                )}
                {user.role === "wholesale" && user.status === "approved" && (
                  <Link to="/account" onClick={() => setOpen(false)} className="text-sm uppercase tracking-widest text-brand-accent">
                    My Account
                  </Link>
                )}
                <button onClick={() => { logout(); setOpen(false); }} className="text-sm uppercase tracking-widest text-brand-neutral text-left">
                  Sign out
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm uppercase tracking-widest text-brand-neutral">
                Sign in
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
